import React, { useMemo, useState } from 'react';
import { AlertCircle, ArrowLeft, CheckCircle, Download, ExternalLink, FileCode, Loader } from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';

function getHtmlReportUrl(report = {}) {
  if (!report || typeof report !== 'object') return '';
  return String(report.viewer_url || report.report_url || report.presigned_url || '').trim();
}

function resolveRunReports(runResult = {}) {
  if (Array.isArray(runResult?.html_reports)) {
    return runResult.html_reports.filter((item) => item && getHtmlReportUrl(item));
  }
  if (runResult?.html_report && getHtmlReportUrl(runResult.html_report)) {
    return [runResult.html_report];
  }
  if (Array.isArray(runResult?.runs)) {
    return runResult.runs
      .map((entry) => entry?.html_report || (entry?.report_url ? { viewer_url: entry.report_url } : null))
      .filter((item) => item && getHtmlReportUrl(item));
  }
  if (runResult?.report_url) {
    return [{ viewer_url: runResult.report_url }];
  }
  return [];
}

export const ExecutionSummaryStep = ({
  editedTestCases,
  runResults,
  pdfId,
  processingResult,
  onBack,
  onExport,
  automationMode = 'pdf',
  downloadActionLabel = 'Download Execution Report',
  showBackButton = true,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [downloadStatus, setDownloadStatus] = useState(null);
  const isFeatureMode = automationMode === 'feature';

  const { executedKeys, failedKeys, caseByKey } = useMemo(() => {
    const cases = [];

    if (Array.isArray(editedTestCases)) {
      editedTestCases.forEach((tc, idx) => {
        cases.push({
          ...tc,
          _category: 'all',
          _case_key: `all::${tc.test_case_no || 'case'}::${idx}`
        });
      });
    } else if (editedTestCases && typeof editedTestCases === 'object') {
      (editedTestCases.positive || []).forEach((tc, idx) => {
        cases.push({
          ...tc,
          _category: 'positive',
          _case_key: `positive::${tc.test_case_no || 'case'}::${idx}`
        });
      });
      (editedTestCases.negative || []).forEach((tc, idx) => {
        cases.push({
          ...tc,
          _category: 'negative',
          _case_key: `negative::${tc.test_case_no || 'case'}::${idx}`
        });
      });
    }

    const map = cases.reduce((acc, tc) => {
      acc[tc._case_key] = tc;
      return acc;
    }, {});

    const keys = Object.keys(runResults || {});
    const failed = keys.filter((key) => (runResults[key]?.exit_code ?? 1) !== 0);

    return { executedKeys: keys, failedKeys: failed, caseByKey: map };
  }, [editedTestCases, runResults]);

  const executedCount = executedKeys.length;
  const failedCount = failedKeys.length;

  const handleDownload = async () => {
    setIsDownloading(true);
    setDownloadError(null);
    setDownloadStatus(null);

    try {
      const data = await onExport();
      const directUrl = String(data?.download_url || data?.downloadUrl || data?.report_url || '').trim();
      if (directUrl) {
        window.open(directUrl, '_blank', 'noopener,noreferrer');
        setDownloadStatus('Execution report is ready.');
        return;
      }
      if (isFeatureMode || !pdfId) {
        setDownloadStatus(data?.message || 'Execution results saved to this feature flow.');
        return;
      }
      const filename = data?.filename || 'TestCases_Execution.xlsx';
      const encodedFilename = encodeURIComponent(filename);
      const sectionNumber = Number(processingResult?.section_number || 0);
      const sectionQuery = Number.isFinite(sectionNumber) && sectionNumber > 0
        ? `?section_number=${encodeURIComponent(sectionNumber)}`
        : '';
      const downloadUrl = `${API_BASE_URL}${API_ENDPOINTS.DOWNLOAD_RESULTS}/${pdfId}/${encodedFilename}${sectionQuery}`;
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setDownloadError(err.message || 'Failed to download execution report');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-xl border-l-4 border-emerald-500 bg-gradient-to-r from-emerald-50 to-green-50 p-4">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-gray-900">Execution Summary</h3>
            <p className="text-sm text-gray-600 mt-1">
              Review execution results and download the report with status per test case.
              {processingResult?.section_title && (
                <span className="block mt-2 text-sm font-medium text-gray-700">
                  Module: {processingResult.section_title}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="soft-card space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-gray-600">Executed</p>
            <p className="text-xl font-bold text-gray-900">{executedCount}</p>
          </div>
          <div className="rounded-lg border border-green-200 bg-green-50 p-3">
            <p className="text-green-700">Passed</p>
            <p className="text-xl font-bold text-green-800">{executedCount - failedCount}</p>
          </div>
          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-red-700">Failed</p>
            <p className="text-xl font-bold text-red-800">{failedCount}</p>
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-semibold text-gray-900">Executed Cases</p>
          {executedKeys.length === 0 ? (
            <p className="text-sm text-gray-600">No scripts have been executed yet.</p>
          ) : (
            <div className="space-y-1 text-sm">
              {executedKeys.map((key) => {
                const tc = caseByKey[key];
                const result = runResults[key] || {};
                const status = (result?.exit_code ?? 1) === 0 ? 'Passed' : 'Failed';
                const reports = resolveRunReports(result);
                return (
                  <div key={`exec-${key}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="min-w-0">
                      <span className="text-gray-900">
                        {tc?.test_case_no || key} — {tc?.test_description || 'Test Case'}
                      </span>
                      {reports.length > 0 && (
                        <p className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-violet-700">
                          <FileCode className="h-3.5 w-3.5" />
                          {reports.length} HTML report{reports.length === 1 ? '' : 's'} available
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {reports[0] && (
                        <a
                          href={getHtmlReportUrl(reports[0])}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-md bg-violet-600 px-2 py-1 text-xs font-semibold text-white hover:bg-violet-700"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Open Report
                        </a>
                      )}
                      <span className={`font-semibold ${status === 'Passed' ? 'text-green-700' : 'text-red-700'}`}>
                        {status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {failedKeys.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-900">Failed Cases</p>
            <div className="space-y-1 text-sm">
              {failedKeys.map((key) => {
                const tc = caseByKey[key];
                return (
                  <div key={`fail-${key}`} className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                    <span className="text-gray-900">
                      {tc?.test_case_no || key} — {tc?.test_description || 'Test Case'}
                    </span>
                    <span className="font-semibold text-red-700">Failed</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {downloadError && (
          <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-600">{downloadError}</p>
          </div>
        )}
        {downloadStatus && (
          <div className="flex gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
            <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-emerald-700">{downloadStatus}</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between border-t border-slate-200 pt-6">
        {showBackButton && onBack ? (
          <button
            onClick={onBack}
            className="secondary-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        ) : <span />}

        <button
          onClick={handleDownload}
          disabled={isDownloading}
          className="primary-btn bg-gradient-to-r from-emerald-600 to-green-600"
        >
          {isDownloading ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              Preparing...
            </>
          ) : (
            <>
              <Download className="w-4 h-4" />
              {downloadActionLabel}
            </>
          )}
        </button>
      </div>

    </div>
  );
};
